import type { ClientLotCSVRow } from "api-types/lots/lotsRequests";
import type { ProcureCostCodeInput } from "api-types/procure/procureCostCodeRequests";
import type { ProcureResourceInput } from "api-types/procure/procureResourceRequests";
import type { ProcureResourceTagInput } from "api-types/procure/procureResourceTagRequests";

export type RulesetType = "lot" | "procureCostCodes" | "procureResources" | "procureResourceTags";

interface Ruleset {
  required: string[];
  optional: string[];
}

/**
 * CSV header names for the Procure rulesets. The values are the literal keys the upload parser
 * reads (headers are lowercased on parse), so they cannot be renamed independently of
 * `ProcureResourceInput` / `ProcureCostCodeInput`. `satisfies` keeps each value narrowed to its
 * literal type, so the arrays below stay assignable and an unknown key stays a compile error.
 */
const COLUMNS_MAPPINGS = {
  ITEMS_NUMBER: "item#",
  ITEMS_DESCRIPTION: "description",
  ITEMS_FACTOR: "factor",
  ITEMS_U_M: "u/m",
  ITEMS_COST: "cost",
  ITEMS_ROUND_TO_NEAREST: "round to nearest",
  ITEMS_ITEM_GROUP: "item group",
  ITEMS_ITEM_GROUP_DESCRIPTION: "item group description",
  ITEMS_TAXABLE: "taxable",
  ITEMS_SHOULD_ORDER: "should order",
  ITEMS_OTHER: "other",
  COST_CODE: "cost code",
  COST_CODE_DESCRIPTION: "description",
} satisfies Record<string, keyof ProcureResourceInput | keyof ProcureCostCodeInput>;

/**
 * LOTS
 */

export const requiredLotHeaders: Array<keyof ClientLotCSVRow> = ["lot number"];

export const optionalLotHeaders: Array<keyof ClientLotCSVRow> = [
  "lot id",
  "external id",
  "phase",
  "block",
  "spec",
  "premium",
  "status",
  "allowed plans",
  "address",
  "area",
  "depth",
  "lot features",
  "lot types",
  "released at",
  "series",
  "width",
  "garage handing",
  "external url",
];

/**
 * PROCURE
 */
export const requiredProcureCostCodeHeaders: Array<keyof ProcureCostCodeInput> = [COLUMNS_MAPPINGS.COST_CODE];
export const requiredProcureResourceTagHeaders: Array<keyof ProcureResourceTagInput> = ["value"];
export const optionalProcureResourceTagHeaders: Array<keyof ProcureResourceTagInput> = ["category", "description"];
export const optionalProcureCostCodeHeaders: Array<keyof ProcureCostCodeInput> = [
  COLUMNS_MAPPINGS.COST_CODE_DESCRIPTION,
];
export const requiredProcureResourceHeaders: Array<keyof ProcureResourceInput> = [
  COLUMNS_MAPPINGS.ITEMS_NUMBER,
  COLUMNS_MAPPINGS.ITEMS_U_M,
];
export const optionalProcureResourceHeaders: Array<keyof ProcureResourceInput> = [
  COLUMNS_MAPPINGS.ITEMS_COST,
  COLUMNS_MAPPINGS.ITEMS_DESCRIPTION,
  COLUMNS_MAPPINGS.ITEMS_FACTOR,
  COLUMNS_MAPPINGS.ITEMS_ITEM_GROUP,
  COLUMNS_MAPPINGS.ITEMS_ITEM_GROUP_DESCRIPTION,
  COLUMNS_MAPPINGS.ITEMS_SHOULD_ORDER,
  COLUMNS_MAPPINGS.ITEMS_OTHER,
  COLUMNS_MAPPINGS.ITEMS_TAXABLE,
  COLUMNS_MAPPINGS.ITEMS_ROUND_TO_NEAREST,
];
/**
 * Column order for the Items CSV template. Mirrors the Items table column order
 * (lib/components/procure/tables/ResourcesTable.tsx) so a downloaded template lines up with a
 * downloaded item list. Order only — required/optional validation is unchanged, and upload
 * parsing is keyed by header name, not position.
 */
export const procureResourceTemplateHeaders: Array<keyof ProcureResourceInput> = [
  COLUMNS_MAPPINGS.ITEMS_NUMBER,
  COLUMNS_MAPPINGS.ITEMS_DESCRIPTION,
  COLUMNS_MAPPINGS.ITEMS_FACTOR,
  COLUMNS_MAPPINGS.ITEMS_U_M,
  COLUMNS_MAPPINGS.ITEMS_COST,
  COLUMNS_MAPPINGS.ITEMS_ROUND_TO_NEAREST,
  COLUMNS_MAPPINGS.ITEMS_ITEM_GROUP,
  COLUMNS_MAPPINGS.ITEMS_ITEM_GROUP_DESCRIPTION,
  COLUMNS_MAPPINGS.ITEMS_TAXABLE,
  COLUMNS_MAPPINGS.ITEMS_SHOULD_ORDER,
  COLUMNS_MAPPINGS.ITEMS_OTHER,
];

export const CSVFormattingRuleSets: { [K in RulesetType]: Ruleset } = {
  lot: {
    required: requiredLotHeaders,
    optional: optionalLotHeaders,
  },
  procureCostCodes: {
    required: requiredProcureCostCodeHeaders,
    optional: optionalProcureCostCodeHeaders,
  },
  procureResources: {
    required: requiredProcureResourceHeaders,
    optional: optionalProcureResourceHeaders,
  },
  procureResourceTags: {
    required: requiredProcureResourceTagHeaders,
    optional: optionalProcureResourceTagHeaders,
  },
};

export const TEMPLATE_FILENAMES: Record<RulesetType, string> = {
  lot: "lot-template.csv",
  procureCostCodes: "CostCodes-template.csv",
  procureResources: "Items-template.csv",
  procureResourceTags: "CustomFields-template.csv",
};

export function TEMPLATE_CODE_DESCRIPTION(type: "Master Plan" | "Location") {
  return `This ID is used to produce Option Templates at the ${type} level to support overrides from the Master Option data specific to this ${type}.`;
}

export function validateHeaders({ headers, type }: { headers: string[]; type: RulesetType }) {
  const { optional, required } = CSVFormattingRuleSets[type];
  return {
    duplicate: headers.filter((header, index) => headers.indexOf(header) !== index),
    missing: required.filter((header) => !headers.includes(header)),
    invalid: headers.filter((header) => ![...required, ...optional].includes(header)),
  };
}
